// src/pages/UserLogReport.jsx
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../utils/api";
import { useAuth } from "../context/AuthContext";
import { useDarkMode, DM } from "../context/DarkModeContext";
import DashboardHeader from "../components/DashboardHeader";

export default function UserLogReport() {
  const navigate = useNavigate();
  const { logout, currentUser } = useAuth();
  const { darkMode, toggleDarkMode } = useDarkMode();
  const d = darkMode;

  const [userList, setUserList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState("all");

  useEffect(() => {
    fetchAll();
  }, []);

  async function fetchAll() {
    setLoading(true);
    try {
      const res = await api.get("/users?orderBy=name");
      setUserList(
        Array.isArray(res)
          ? res.map((row) => ({
              id: row.id,
              ...(typeof row.data === "string" ? JSON.parse(row.data) : row.data || row),
            }))
          : []
      );
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  }

  async function handleLogout() {
    await logout();
    navigate("/admin");
  }

  const filtered = userList
    .filter(u => {
      if (filterType === "inactive") return u.status === "inactive";
      if (filterType === "active") return u.status !== "inactive";
      return true;
    })
    .filter(u => {
      const s = searchTerm.toLowerCase();
      return (
        u.name?.toLowerCase().includes(s) ||
        u.email?.toLowerCase().includes(s) ||
        u.department?.toLowerCase().includes(s)
      );
    })
    .sort((a, b) => {
      const dateA = a.lastLogin ? new Date(a.lastLogin).getTime() : 0;
      const dateB = b.lastLogin ? new Date(b.lastLogin).getTime() : 0;
      return dateB - dateA; // Most recent first
    });

  const activeCount = userList.filter(u => u.status !== "inactive").length;
  const inactiveCount = userList.filter(u => u.status === "inactive").length;

  return (
    <div style={{ minHeight: "100vh", background: DM.pageBg(d), fontFamily: "'Nunito', sans-serif" }}>
      <DashboardHeader label="User Logs">
        <button onClick={() => navigate("/admin/dashboard")} style={{ background: "#fff", color: "#1d4ed8", border: "none", borderRadius: 10, padding: "8px 18px", fontWeight: 800, fontSize: 16, cursor: "pointer" }}>← Dashboard</button>
        <button onClick={toggleDarkMode} style={{ background: "rgba(255,255,255,0.15)", color: "#fff", border: "1px solid rgba(255,255,255,0.3)", borderRadius: 10, padding: "8px 14px", fontWeight: 600, fontSize: 15, cursor: "pointer" }}>{darkMode ? "☀️ Light" : "🌙 Dark"}</button>
        <button onClick={handleLogout} style={{ background: "rgba(255,255,255,0.15)", color: "#fff", border: "1px solid rgba(255,255,255,0.3)", borderRadius: 10, padding: "8px 14px", fontWeight: 600, fontSize: 15, cursor: "pointer" }}>🚪 Logout</button>
      </DashboardHeader>

      <div style={{ padding: "28px 32px" }}>
        
        {/* Stats */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 16, marginBottom: 24 }}>
          <div style={{ background: "linear-gradient(135deg, #10b981, #059669)", borderRadius: 16, padding: "24px", color: "#fff", boxShadow: "0 4px 16px rgba(16,185,129,0.3)" }}>
            <div style={{ fontSize: 32 }}>✅</div>
            <div style={{ fontSize: 36, fontWeight: 900, marginTop: 8 }}>{activeCount}</div>
            <div style={{ fontSize: 16, fontWeight: 700, opacity: 0.9 }}>Active Users</div>
          </div>
          <div style={{ background: "linear-gradient(135deg, #64748b, #475569)", borderRadius: 16, padding: "24px", color: "#fff", boxShadow: "0 4px 16px rgba(100,116,139,0.3)" }}>
            <div style={{ fontSize: 32 }}>⛔</div>
            <div style={{ fontSize: 36, fontWeight: 900, marginTop: 8 }}>{inactiveCount}</div>
            <div style={{ fontSize: 16, fontWeight: 700, opacity: 0.9 }}>Inactive Users</div>
          </div>
          <div style={{ background: "linear-gradient(135deg, #3b82f6, #2563eb)", borderRadius: 16, padding: "24px", color: "#fff", boxShadow: "0 4px 16px rgba(59,130,246,0.3)" }}>
            <div style={{ fontSize: 32 }}>👥</div>
            <div style={{ fontSize: 36, fontWeight: 900, marginTop: 8 }}>{userList.length}</div>
            <div style={{ fontSize: 16, fontWeight: 700, opacity: 0.9 }}>Total Registered Users</div>
          </div>
        </div>

        {/* Filters */}
        <div style={{ background: DM.cardBg(d), borderRadius: 16, padding: "16px 20px", marginBottom: 20, boxShadow: DM.shadow(d), display: "flex", gap: 16, alignItems: "center" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, background: DM.inputBg(d), border: `1.5px solid ${DM.inputBorder(d)}`, borderRadius: 12, padding: "10px 16px", flex: 1 }}>
            <span>🔍</span>
            <input 
              value={searchTerm} 
              onChange={e => setSearchTerm(e.target.value)} 
              placeholder="Search by name, email, or department..."
              style={{ background: "transparent", border: "none", outline: "none", fontSize: 16, color: DM.textPrimary(d), width: "100%" }} 
            />
          </div>
          <select 
            value={filterType} 
            onChange={e => setFilterType(e.target.value)}
            style={{ background: DM.inputBg(d), border: `1.5px solid ${DM.inputBorder(d)}`, borderRadius: 12, padding: "10px 16px", fontSize: 16, outline: "none", cursor: "pointer", color: DM.textPrimary(d) }}
          >
            <option value="all">All Users</option>
            <option value="active">✅ Active Only</option>
            <option value="inactive">⛔ Inactive Only</option>
          </select>
          <button onClick={fetchAll} style={{ background: "#f1f5f9", color: "#475569", border: "none", borderRadius: 12, padding: "12px 20px", fontWeight: 800, fontSize: 16, cursor: "pointer" }}>
            🔄 Refresh
          </button>
        </div>

        {/* Table */}
        <div style={{ background: DM.cardBg(d), borderRadius: 16, boxShadow: DM.shadow(d), overflow: "hidden" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 16 }}>
            <thead>
              <tr style={{ background: d ? "#0f172a" : "#f8fafc" }}>
                <th style={{ padding: "16px 20px", textAlign: "left", color: d ? "#94a3b8" : "#64748b", borderBottom: `1px solid ${DM.border(d)}` }}>User Details</th>
                <th style={{ padding: "16px 20px", textAlign: "left", color: d ? "#94a3b8" : "#64748b", borderBottom: `1px solid ${DM.border(d)}` }}>Role / Department</th>
                <th style={{ padding: "16px 20px", textAlign: "center", color: d ? "#94a3b8" : "#64748b", borderBottom: `1px solid ${DM.border(d)}` }}>Current Status</th>
                <th style={{ padding: "16px 20px", textAlign: "right", color: d ? "#94a3b8" : "#64748b", borderBottom: `1px solid ${DM.border(d)}` }}>Last Login Date & Time</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={4} style={{ padding: 40, textAlign: "center", color: "#94a3b8" }}>⏳ Loading data...</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={4} style={{ padding: 40, textAlign: "center", color: "#94a3b8" }}>No users found.</td></tr>
              ) : filtered.map((u, i) => (
                <tr key={u.id} style={{ borderBottom: `1px solid ${DM.border(d)}`, transition: "background 0.2s" }} onMouseEnter={e => e.currentTarget.style.background = d ? "#1e293b" : "#f8fafc"} onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                  <td style={{ padding: "16px 20px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                      <div style={{ width: 40, height: 40, borderRadius: "50%", background: `hsl(${i*40}, 65%, 55%)`, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: 18 }}>
                        {u.name?.[0]?.toUpperCase()}
                      </div>
                      <div>
                        <div style={{ fontWeight: 800, color: DM.textPrimary(d), fontSize: 16 }}>{u.title || ""} {u.name}</div>
                        <div style={{ fontSize: 14, color: DM.textSecondary(d) }}>{u.email}</div>
                      </div>
                    </div>
                  </td>
                  <td style={{ padding: "16px 20px" }}>
                    <div style={{ fontWeight: 700, color: DM.textPrimary(d), textTransform: "capitalize" }}>{u.role}</div>
                    <div style={{ fontSize: 14, color: DM.textSecondary(d) }}>{u.department || "N/A"}</div>
                  </td>
                  <td style={{ padding: "16px 20px", textAlign: "center" }}>
                    {u.status === "inactive" ? (
                      <span style={{ background: "#fef2f2", color: "#dc2626", padding: "4px 12px", borderRadius: 20, fontWeight: 700, fontSize: 14 }}>⛔ Inactive</span>
                    ) : (
                      <span style={{ background: "#eaf1ff", color: "#2563eb", padding: "4px 12px", borderRadius: 20, fontWeight: 700, fontSize: 14 }}>✅ Active</span>
                    )}
                  </td>
                  <td style={{ padding: "16px 20px", textAlign: "right" }}>
                    <div style={{ fontWeight: 700, color: DM.textPrimary(d), fontSize: 15 }}>
                      {u.lastLogin ? new Date(u.lastLogin).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : "Never Logged In"}
                    </div>
                    {u.lastLogin && (
                      <div style={{ fontSize: 13, color: DM.textSecondary(d) }}>
                        {new Date(u.lastLogin).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true })}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
